import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { api, formatErr } from "@/lib/api";
import Modal, { inputCls, inputErrCls, FieldError, labelCls, btnPrimary, btnAccent, btnOutline } from "@/components/Modal";
import { theadCls, thCls, tdCls, rowCls } from "@/components/Table";
import { useFormErrors } from "@/lib/formErrors";
import { Plus, Trash2, Save, CloudUpload, DatabaseBackup, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const isAdmin = user?.role === "admin";
  const canEditShop = user?.role === "admin" || user?.role === "manager";
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "salesperson" });
  const [shop, setShop] = useState({ name: "", address: "", phone: "", tax_id: "" });
  const [pw, setPw] = useState({ current_password: "", new_password: "" });

  const { data: shopData } = useQuery({
    queryKey: ["shop-settings"],
    queryFn: () => api.get("/settings/shop").then((r) => r.data),
  });
  useEffect(() => { if (shopData) setShop(shopData); }, [shopData]);

  const saveShop = useMutation({
    mutationFn: () => api.put("/settings/shop", shop),
    onSuccess: () => { toast.success("Shop settings saved"); qc.invalidateQueries({ queryKey: ["shop-settings"] }); },
    onError: (e) => toast.error(formatErr(e.response?.data?.detail)),
  });

  const changePw = useMutation({
    mutationFn: () => api.post("/auth/change-password", pw),
    onSuccess: () => { toast.success("Password changed"); setPw({ current_password: "", new_password: "" }); },
    onError: (e) => toast.error(formatErr(e.response?.data?.detail)),
  });

  const { data: users = [] } = useQuery({
    queryKey: ["users"],
    queryFn: () => api.get("/auth/users").then((r) => r.data),
    enabled: isAdmin,
  });

  const { data: backup } = useQuery({
    queryKey: ["backup-status"],
    queryFn: () => api.get("/backup/status").then((r) => r.data),
    enabled: isAdmin,
  });
  const runBackup = useMutation({
    mutationFn: () => api.post("/backup/run"),
    onSuccess: () => {
      toast.success("Backup uploaded to Google Drive");
      qc.invalidateQueries({ queryKey: ["backup-status"] });
      qc.invalidateQueries({ queryKey: ["backup-list"] });
      qc.invalidateQueries({ queryKey: ["backup-quota"] });
    },
    onError: (e) => toast.error(formatErr(e.response?.data?.detail)),
  });

  const fe = useFormErrors();
  const create = useMutation({
    mutationFn: () => api.post("/auth/users", form),
    onSuccess: () => { toast.success("User added"); qc.invalidateQueries({ queryKey: ["users"] }); setOpen(false); setForm({ name: "", email: "", password: "", role: "salesperson" }); fe.clear(); },
    onError: (e) => fe.handleError(e, toast.error),
  });
  const del = useMutation({
    mutationFn: (id) => api.delete(`/auth/users/${id}`),
    onSuccess: () => { toast.success("User deleted"); qc.invalidateQueries({ queryKey: ["users"] }); },
    onError: (e) => toast.error(formatErr(e.response?.data?.detail)),
  });

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <div className="label-cap">Shop</div>
        <h1 className="font-heading text-3xl mt-1">Settings</h1>
      </div>

      <div className="card-soft p-6">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-heading text-xl">Shop Information</h3>
          {canEditShop && (
            <button data-testid="save-shop-btn" onClick={() => saveShop.mutate()} disabled={saveShop.isPending} className={btnPrimary + " flex items-center gap-2"}>
              <Save size={14} /> Save
            </button>
          )}
        </div>
        <p className="text-xs text-agri-ink-2 mb-4">Shown on every invoice and client statement PDF.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><label className={labelCls}>Shop Name</label>
            <input data-testid="shop-name-input" disabled={!canEditShop} value={shop.name} onChange={(e) => setShop({ ...shop, name: e.target.value })} className={inputCls} /></div>
          <div><label className={labelCls}>Phone</label>
            <input disabled={!canEditShop} value={shop.phone} onChange={(e) => setShop({ ...shop, phone: e.target.value })} className={inputCls} /></div>
          <div className="md:col-span-2"><label className={labelCls}>Address</label>
            <input disabled={!canEditShop} value={shop.address} onChange={(e) => setShop({ ...shop, address: e.target.value })} className={inputCls} /></div>
          <div><label className={labelCls}>Tax / GST Number</label>
            <input disabled={!canEditShop} value={shop.tax_id} onChange={(e) => setShop({ ...shop, tax_id: e.target.value })} className={inputCls} /></div>
        </div>
      </div>

      <div className="card-soft p-6">
        <h3 className="font-heading text-xl">Profile</h3>
        <div className="grid grid-cols-2 gap-4 mt-4 text-sm">
          <div><div className="label-cap">Name</div><div className="mt-1">{user?.name}</div></div>
          <div><div className="label-cap">Email</div><div className="mt-1">{user?.email}</div></div>
          <div><div className="label-cap">Role</div><div className="mt-1 capitalize">{user?.role}</div></div>
        </div>
      </div>

      <div className="card-soft p-6" data-testid="change-password-card">
        <h3 className="font-heading text-xl">Change Password</h3>
        <p className="text-xs text-agri-ink-2 mb-4">Update the password for your own account.</p>
        <form
          onSubmit={(e) => { e.preventDefault(); changePw.mutate(); }}
          className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end"
        >
          <div><label className={labelCls}>Current password</label>
            <input data-testid="current-password-input" required type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} className={inputCls} /></div>
          <div><label className={labelCls}>New password</label>
            <input data-testid="new-password-input" required type="password" minLength={6} value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className={inputCls} /></div>
          <div className="md:col-span-2 flex justify-end">
            <button data-testid="change-password-btn" type="submit" disabled={changePw.isPending} className={btnPrimary}>
              {changePw.isPending ? "Updating…" : "Update password"}
            </button>
          </div>
        </form>
      </div>

      {isAdmin && (
        <div className="card-soft p-6" data-testid="backup-card">
          <div className="flex items-center justify-between">
            <h3 className="font-heading text-xl flex items-center gap-2">
              <DatabaseBackup size={18} className="text-agri-forest" /> Data Backup
            </h3>
            {backup?.connected ? (
              <span className="inline-flex items-center gap-1.5 text-sm text-agri-forest font-medium">
                <CheckCircle2 size={16} /> Google Drive connected
              </span>
            ) : (
              <span className="text-sm text-agri-ink-2">Not connected</span>
            )}
          </div>
          <p className="text-xs text-agri-ink-2 mt-1 mb-4">
            {backup?.connected
              ? "Save a full copy of your database to your Google Drive."
              : "Connect Google Drive to back up your database."}
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              data-testid="settings-backup-now-btn"
              onClick={() => runBackup.mutate()}
              disabled={!backup?.connected || !backup?.tools_available || runBackup.isPending}
              className={btnPrimary + " flex items-center gap-2"}
              title={!backup?.connected ? "Connect Google Drive first" : "Back up now"}
            >
              <CloudUpload size={16} /> {runBackup.isPending ? "Backing up…" : "Back up now"}
            </button>
            <Link to="/settings/backups" className={btnOutline + " flex items-center gap-2"}>
              <DatabaseBackup size={15} /> {backup?.connected ? "Manage backups" : "Set up backups"}
            </Link>
          </div>
        </div>
      )}

      {isAdmin && (
        <div className="card-soft">
          <div className="p-5 flex items-center justify-between border-b border-[rgba(28,28,30,0.06)]">
            <h3 className="font-heading text-xl">User Management</h3>
            <button data-testid="add-user-btn" onClick={() => { fe.clear(); setOpen(true); }} className={btnAccent + " flex items-center gap-2"}><Plus size={16} /> Add User</button>
          </div>
          <table className="w-full text-sm">
            <thead className={theadCls}>
              <tr><th className={thCls}>Name</th><th className={thCls}>Email</th><th className={thCls}>Role</th><th className={thCls}></th></tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className={rowCls}>
                  <td className={`${tdCls} font-medium`}>{u.name}</td>
                  <td className={`${tdCls} text-agri-ink-2`}>{u.email}</td>
                  <td className={tdCls}><span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-50 text-agri-amber-dark capitalize">{u.role}</span></td>
                  <td className={`${tdCls} text-right`}>
                    {u.id !== user.id && <button onClick={() => window.confirm("Delete user?") && del.mutate(u.id)} className="text-agri-ink-2 hover:text-agri-danger"><Trash2 size={15} /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Add Staff User">
        <form onSubmit={(e) => { e.preventDefault(); create.mutate(); }} className="space-y-3">
          <div>
            <label className={labelCls}>Name</label>
            <input required value={form.name} onChange={(e) => { setForm({ ...form, name: e.target.value }); fe.clearField("name"); }} className={inputCls + (fe.fieldError("name") ? " " + inputErrCls : "")} />
            <FieldError>{fe.fieldError("name")}</FieldError>
          </div>
          <div>
            <label className={labelCls}>Email</label>
            <input required type="email" value={form.email} onChange={(e) => { setForm({ ...form, email: e.target.value }); fe.clearField("email"); }} className={inputCls + (fe.fieldError("email") ? " " + inputErrCls : "")} />
            <FieldError>{fe.fieldError("email")}</FieldError>
          </div>
          <div>
            <label className={labelCls}>Password</label>
            <input required type="password" minLength={6} value={form.password} onChange={(e) => { setForm({ ...form, password: e.target.value }); fe.clearField("password"); }} className={inputCls + (fe.fieldError("password") ? " " + inputErrCls : "")} />
            <FieldError>{fe.fieldError("password")}</FieldError>
          </div>
          <div><label className={labelCls}>Role</label>
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={inputCls}>
              <option value="admin">Admin</option><option value="manager">Manager</option><option value="salesperson">Salesperson</option>
            </select></div>
          <div className="flex justify-end gap-2 pt-2"><button type="button" onClick={() => setOpen(false)} className={btnOutline}>Cancel</button><button className={btnPrimary} disabled={create.isPending}>Add User</button></div>
        </form>
      </Modal>
    </div>
  );
}
